// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../supabase";
import { useAuth } from "../AuthContext";

const CHANNELS = [
  { id: "general", label: "# general", description: "General team discussion" },
  { id: "dev", label: "# dev", description: "Development & engineering" },
  { id: "ideas", label: "# ideas", description: "Ideas & brainstorming" },
];

const FORGE_ORANGE = "#E86833";
const FORGE_ORANGE_DIM = "#E8683322";

function formatTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (isToday) return time;
  return d.toLocaleDateString([], { month: "short", day: "numeric" }) + " " + time;
}

function MessageBubble({ msg, onSendToClaude, sending }) {
  const isClaude = msg.is_claude_response;
  return (
    <div style={{
      display: "flex",
      flexDirection: isClaude ? "row" : "row",
      alignItems: "flex-start",
      gap: 10,
      marginBottom: 14,
    }}>
      {/* Avatar */}
      <div style={{
        width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
        background: isClaude ? FORGE_ORANGE : "#1e293b",
        border: isClaude ? `2px solid ${FORGE_ORANGE}` : "2px solid #334155",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 13, fontWeight: 700, color: isClaude ? "#fff" : "#94a3b8",
      }}>
        {isClaude ? "AI" : (msg.display_name || "?").slice(0, 2).toUpperCase()}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Name + time */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
          <span style={{
            fontSize: 13, fontWeight: 700,
            color: isClaude ? FORGE_ORANGE : "#e2e8f0",
          }}>
            {isClaude ? "Claude (Forge AI)" : msg.display_name}
          </span>
          {isClaude && (
            <span style={{
              fontSize: 10, fontWeight: 700, color: FORGE_ORANGE,
              background: FORGE_ORANGE_DIM, padding: "1px 7px", borderRadius: 10,
              textTransform: "uppercase", letterSpacing: 0.5,
            }}>AI</span>
          )}
          <span style={{ fontSize: 11, color: "#475569" }}>{formatTime(msg.created_at)}</span>
          {msg.claude_task_id && (
            <span style={{ fontSize: 10, color: "#475569" }}>
              task: {msg.claude_task_id.slice(0, 8)}
            </span>
          )}
        </div>

        {/* Message bubble */}
        <div style={{
          background: isClaude ? "#1a1f2e" : "#1e293b",
          border: isClaude ? `1px solid ${FORGE_ORANGE}44` : "1px solid #334155",
          borderRadius: isClaude ? "4px 12px 12px 12px" : "12px 12px 12px 4px",
          padding: "10px 14px",
          fontSize: 14, color: "#e2e8f0", lineHeight: 1.6,
          maxWidth: "100%", wordBreak: "break-word",
          whiteSpace: "pre-wrap",
        }}>
          {msg.message}
        </div>

        {/* Send to Claude button (only on non-Claude messages) */}
        {!isClaude && (
          <button
            onClick={() => onSendToClaude(msg)}
            disabled={sending}
            style={{
              marginTop: 6, padding: "4px 12px",
              background: "transparent",
              border: `1px solid ${FORGE_ORANGE}88`,
              borderRadius: 6, color: FORGE_ORANGE,
              fontSize: 11, fontWeight: 600, cursor: sending ? "not-allowed" : "pointer",
              opacity: sending ? 0.5 : 1,
              transition: "all 0.15s",
              letterSpacing: 0.3,
            }}
            title="Send this message to Claude for a response"
          >
            {sending ? "Sending..." : "Send to Claude"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function TeamChatPage() {
  const { user } = useAuth();
  const [channel, setChannel] = useState("general");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [claudeSending, setClaudeSending] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const subscriptionRef = useRef(null);

  const displayName = user?.full_name || user?.email?.split("@")[0] || "Team Member";

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Load messages for the current channel
  const loadMessages = useCallback(async (ch) => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("team_chat_messages")
      .select("*")
      .eq("channel", ch)
      .order("created_at", { ascending: true })
      .limit(200);

    if (err) {
      setError("Failed to load messages: " + err.message);
    } else {
      setMessages(data || []);
    }
    setLoading(false);
  }, []);

  // Set up realtime subscription
  useEffect(() => {
    loadMessages(channel);

    // Clean up previous subscription
    if (subscriptionRef.current) {
      supabase.removeChannel(subscriptionRef.current);
    }

    const sub = supabase
      .channel(`team-chat-${channel}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "team_chat_messages",
          filter: `channel=eq.${channel}`,
        },
        (payload) => {
          setMessages((prev) => {
            // Avoid duplicates
            if (prev.find((m) => m.id === payload.new.id)) return prev;
            return [...prev, payload.new];
          });
        }
      )
      .subscribe();

    subscriptionRef.current = sub;

    return () => {
      supabase.removeChannel(sub);
    };
  }, [channel, loadMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setError(null);

    const { error: err } = await supabase.from("team_chat_messages").insert({
      user_id: user?.id,
      display_name: displayName,
      message: text,
      channel,
      is_claude_response: false,
    });

    if (err) {
      setError("Failed to send: " + err.message);
    } else {
      setInput("");
    }
    setSending(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSendToClaude = async (msg) => {
    if (claudeSending) return;
    setClaudeSending(true);
    setError(null);

    const pat = process.env.REACT_APP_GITHUB_PAT;
    if (!pat) {
      setError("REACT_APP_GITHUB_PAT is not configured.");
      setClaudeSending(false);
      return;
    }

    try {
      const res = await fetch(
        "https://api.github.com/repos/jlutz-ship-it/miskulin-technologies/dispatches",
        {
          method: "POST",
          headers: {
            Authorization: `token ${pat}`,
            Accept: "application/vnd.github.v3+json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            event_type: "claude-chat",
            client_payload: {
              task: msg.message,
              message_id: msg.id,
              channel: msg.channel,
              requested_by: displayName,
              user_id: user?.id,
            },
          }),
        }
      );

      if (!res.ok) {
        const body = await res.text();
        throw new Error(`GitHub API ${res.status}: ${body}`);
      }

      // Optimistically insert a pending indicator
      await supabase.from("team_chat_messages").insert({
        user_id: null,
        display_name: "System",
        message: `Claude is working on: "${msg.message.slice(0, 80)}${msg.message.length > 80 ? "..." : ""}"`,
        channel,
        is_claude_response: false,
        claude_task_id: msg.id,
      });
    } catch (err) {
      setError("Failed to contact Claude: " + err.message);
    }

    setClaudeSending(false);
  };

  const activeChannel = CHANNELS.find((c) => c.id === channel);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 120px)", minHeight: 400, gap: 0 }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 0, paddingBottom: 12 }}>
        <div>
          <h1 style={{ margin: 0 }}>Forge Team Chat</h1>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>
            {activeChannel?.description} &middot; Real-time internal communications
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            fontSize: 11, fontWeight: 700, color: FORGE_ORANGE,
            background: FORGE_ORANGE_DIM, padding: "4px 12px", borderRadius: 20,
            textTransform: "uppercase", letterSpacing: 0.5, border: `1px solid ${FORGE_ORANGE}44`,
          }}>LIVE</span>
        </div>
      </div>

      <div style={{ display: "flex", flex: 1, gap: 0, minHeight: 0 }}>
        {/* Channel Sidebar */}
        <div style={{
          width: 180, flexShrink: 0,
          background: "#0a0f1a",
          borderRight: "1px solid #1e293b",
          display: "flex", flexDirection: "column",
          padding: "16px 0",
        }}>
          <div style={{
            fontSize: 10, fontWeight: 700, color: "#475569",
            textTransform: "uppercase", letterSpacing: 1,
            padding: "0 16px 8px",
          }}>
            Channels
          </div>
          {CHANNELS.map((ch) => (
            <button
              key={ch.id}
              onClick={() => setChannel(ch.id)}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "8px 16px", border: "none", cursor: "pointer",
                background: channel === ch.id ? `${FORGE_ORANGE}18` : "transparent",
                color: channel === ch.id ? FORGE_ORANGE : "#94a3b8",
                fontSize: 13, fontWeight: channel === ch.id ? 700 : 400,
                borderLeft: channel === ch.id ? `3px solid ${FORGE_ORANGE}` : "3px solid transparent",
                transition: "all 0.15s",
              }}
            >
              {ch.label}
            </button>
          ))}

          {/* Divider */}
          <div style={{ flex: 1 }} />
          <div style={{
            padding: "12px 16px",
            borderTop: "1px solid #1e293b",
            fontSize: 11, color: "#475569", lineHeight: 1.5,
          }}>
            <div style={{ color: "#64748b", fontWeight: 600, marginBottom: 4 }}>You</div>
            <div style={{ color: "#94a3b8" }}>{displayName}</div>
          </div>
        </div>

        {/* Chat Area */}
        <div style={{
          flex: 1, display: "flex", flexDirection: "column",
          background: "#0f172a", minWidth: 0,
        }}>
          {/* Messages */}
          <div style={{
            flex: 1, overflowY: "auto", padding: "20px 20px 8px",
            display: "flex", flexDirection: "column",
          }}>
            {loading ? (
              <div style={{ textAlign: "center", color: "#475569", paddingTop: 40, fontSize: 14 }}>
                Loading messages...
              </div>
            ) : messages.length === 0 ? (
              <div style={{
                textAlign: "center", color: "#475569", paddingTop: 60, fontSize: 14,
              }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>💬</div>
                <div style={{ fontWeight: 600, color: "#64748b", marginBottom: 4 }}>No messages yet</div>
                <div>Be the first to say something in {activeChannel?.label}</div>
              </div>
            ) : (
              messages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  msg={msg}
                  onSendToClaude={handleSendToClaude}
                  sending={claudeSending}
                />
              ))
            )}
            <div ref={bottomRef} />
          </div>

          {/* Error Banner */}
          {error && (
            <div style={{
              margin: "0 20px 8px",
              padding: "10px 14px",
              background: "#dc262622",
              border: "1px solid #dc2626",
              borderRadius: 8, color: "#fca5a5", fontSize: 13,
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                style={{ background: "none", border: "none", color: "#fca5a5", cursor: "pointer", fontSize: 16, lineHeight: 1 }}
              >×</button>
            </div>
          )}

          {/* Input Area */}
          <div style={{
            padding: "12px 20px 16px",
            borderTop: "1px solid #1e293b",
            background: "#0f172a",
          }}>
            <div style={{
              display: "flex", gap: 8, alignItems: "flex-end",
              background: "#1e293b",
              border: `1px solid #334155`,
              borderRadius: 10,
              padding: "8px 12px",
              transition: "border-color 0.15s",
            }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`Message ${activeChannel?.label}...`}
                rows={1}
                style={{
                  flex: 1, background: "transparent", border: "none",
                  color: "#e2e8f0", fontSize: 14, resize: "none",
                  outline: "none", fontFamily: "inherit", lineHeight: 1.5,
                  maxHeight: 120, overflow: "auto",
                }}
                onInput={(e) => {
                  e.target.style.height = "auto";
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
                }}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || sending}
                style={{
                  padding: "7px 18px",
                  background: input.trim() && !sending ? FORGE_ORANGE : "#334155",
                  color: input.trim() && !sending ? "#fff" : "#64748b",
                  border: "none", borderRadius: 7,
                  fontSize: 13, fontWeight: 600, cursor: input.trim() && !sending ? "pointer" : "not-allowed",
                  transition: "all 0.15s", flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
              >
                Send
              </button>
            </div>
            <div style={{ fontSize: 11, color: "#475569", marginTop: 6, paddingLeft: 2 }}>
              Enter to send &middot; Shift+Enter for new line &middot; Click "Send to Claude" on any message to get an AI response
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
